import { Component } from '@theme/component';

class CustomCreateYourLook extends Component {
  #desktopIndex = 0;
  #heroIndex = 0;
  #productIndex = 0;
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver = new ResizeObserver(() => this.#updateAll());
    this.#resizeObserver.observe(this);
    this.#updateAll();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.dataset.ready = '';
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  prevDesktop() {
    this.#goDesktop(this.#desktopIndex - 1);
  }

  nextDesktop() {
    this.#goDesktop(this.#desktopIndex + 1);
  }

  prevHero() {
    this.#goHero(this.#heroIndex - 1);
  }

  nextHero() {
    this.#goHero(this.#heroIndex + 1);
  }

  prevProducts() {
    this.#goProducts(this.#productIndex - 1);
  }

  nextProducts() {
    this.#goProducts(this.#productIndex + 1);
  }

  #updateAll() {
    this.#updateDesktop();
    this.#updateHero();
    this.#updateProducts();
  }

  #goDesktop(index) {
    const positions = this.#getDesktopPositions();
    const max = Math.max(0, positions.length - 1);
    this.#desktopIndex = Math.max(0, Math.min(index, max));
    this.#updateDesktop();
  }

  #goHero(index) {
    const slides = this.refs.mobileHeroSlides || [];
    const max = Math.max(0, slides.length - 1);
    this.#heroIndex = Math.max(0, Math.min(index, max));
    this.#updateHero();
  }

  #goProducts(index) {
    const slides = this.refs.mobileProductSlides || [];
    const visible = this.#getMobileProductColumns();
    const max = Math.max(0, slides.length - visible);
    this.#productIndex = Math.max(0, Math.min(index, max));
    this.#updateProducts();
  }

  #getDesktopPositions() {
    const viewport = this.refs.desktopViewport;
    const panels = this.refs.desktopPanels || [];
    const track = this.refs.desktopTrack;

    if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || !panels.length) return [0];

    const maxOffset = Math.max(0, track.scrollWidth - viewport.clientWidth);
    const positions = panels.map((panel) => Math.min(panel.offsetLeft, maxOffset));
    const unique = [...new Set(positions)];

    if (!unique.includes(maxOffset)) unique.push(maxOffset);

    return unique.sort((a, b) => a - b);
  }

  #updateDesktop() {
    const track = this.refs.desktopTrack;
    const positions = this.#getDesktopPositions();
    const max = Math.max(0, positions.length - 1);

    this.#desktopIndex = Math.max(0, Math.min(this.#desktopIndex, max));

    if (track instanceof HTMLElement) {
      track.style.setProperty('--mcl-desktop-offset', `${-positions[this.#desktopIndex]}px`);
    }

    if (this.refs.desktopPrev instanceof HTMLButtonElement) {
      this.refs.desktopPrev.disabled = this.#desktopIndex <= 0;
    }

    if (this.refs.desktopNext instanceof HTMLButtonElement) {
      this.refs.desktopNext.disabled = this.#desktopIndex >= max;
    }
  }

  #updateHero() {
    const track = this.refs.mobileHeroTrack;
    const viewport = this.refs.mobileHeroViewport;
    const slides = this.refs.mobileHeroSlides || [];
    const max = Math.max(0, slides.length - 1);

    this.#heroIndex = Math.max(0, Math.min(this.#heroIndex, max));

    if (track instanceof HTMLElement && viewport instanceof HTMLElement) {
      track.style.setProperty('--mcl-mobile-hero-offset', `${-this.#heroIndex * viewport.clientWidth}px`);
    }

    if (this.refs.mobileHeroPrev instanceof HTMLButtonElement) {
      this.refs.mobileHeroPrev.disabled = this.#heroIndex <= 0;
    }

    if (this.refs.mobileHeroNext instanceof HTMLButtonElement) {
      this.refs.mobileHeroNext.disabled = this.#heroIndex >= max;
    }
  }

  #getMobileProductColumns() {
    return parseInt(getComputedStyle(this).getPropertyValue('--mcl-mobile-product-columns').trim(), 10) || 3;
  }

  #updateProducts() {
    const track = this.refs.mobileProductsTrack;
    const slides = this.refs.mobileProductSlides || [];
    const visible = this.#getMobileProductColumns();
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--mcl-mobile-gap')) || 0;
    const max = Math.max(0, slides.length - visible);

    this.#productIndex = Math.max(0, Math.min(this.#productIndex, max));

    if (track instanceof HTMLElement && slides.length) {
      const slideWidth = slides[0].offsetWidth;
      const offset = this.#productIndex * (slideWidth + gap);
      track.style.setProperty('--mcl-mobile-products-offset', `${-offset}px`);
    }

    if (this.refs.mobileProductsPrev instanceof HTMLButtonElement) {
      this.refs.mobileProductsPrev.disabled = this.#productIndex <= 0;
    }

    if (this.refs.mobileProductsNext instanceof HTMLButtonElement) {
      this.refs.mobileProductsNext.disabled = this.#productIndex >= max;
    }
  }
}

if (!customElements.get('custom-create-your-look')) {
  customElements.define('custom-create-your-look', CustomCreateYourLook);
}
